import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fmtError } from "@/lib/api";
import { MailX, Check, Loader2, AlertCircle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Disiscrizione dalle promozioni: la cliente arriva qui dal link in fondo alle
 * email del centro (/:slug/disiscriviti/:token). Pagina pubblica, senza login.
 * Il token è firmato dal gestionale: il sito registra la richiesta e il
 * gestionale la applica come revoca del consenso (niente più messaggi).
 * Serve un click di conferma: gli antivirus delle email aprono i link da soli.
 */
export default function UnsubscribePage() {
  const { slug, token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${BASE}/public/unsubscribe/${slug}/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data?.already_requested) setDone(true);
      })
      .catch((e) => setError(fmtError(e)))
      .finally(() => setLoading(false));
  }, [slug, token]);

  const conferma = async () => {
    if (sending) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${BASE}/public/unsubscribe/${slug}/${token}`);
      setDone(true);
    } catch (e) {
      setError(fmtError(e));
    } finally {
      setSending(false);
    }
  };

  const Wrap = ({ children }) => (
    <div className="min-h-screen bg-[#FAFAFA] flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <Wrap>
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[#A78BFA]" size={28} />
        </div>
      </Wrap>
    );
  }

  if (!data) {
    return (
      <Wrap>
        <div className="rounded-3xl bg-white border border-black/5 p-8 text-center" data-testid="unsubscribe-error">
          <AlertCircle className="mx-auto text-[#B91C1C] mb-3" size={28} />
          <h1 className="font-serif text-2xl text-[#1A1A1A]">Link non valido</h1>
          <p className="mt-2 text-sm text-[#4A4A4A] font-light">{error}</p>
        </div>
      </Wrap>
    );
  }

  if (done) {
    return (
      <Wrap>
        <div className="rounded-3xl bg-white border border-black/5 p-8 text-center" data-testid="unsubscribe-done">
          <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] flex items-center justify-center mb-4">
            <Check className="text-white" size={26} />
          </div>
          <h1 className="font-serif text-3xl text-[#1A1A1A]">Fatto</h1>
          <p className="mt-3 text-[#4A4A4A] font-light">
            Non riceverai più promozioni via {data.channel_label} da <strong>{data.center}</strong>.
          </p>
          <p className="mt-4 text-xs text-[#737373]">
            Continuerai a ricevere solo i messaggi legati ai tuoi appuntamenti. Se cambi idea, dillo al centro.
          </p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="rounded-3xl bg-white border border-black/5 p-7" data-testid="unsubscribe-form">
        <div className="flex items-center gap-2 text-[#A78BFA] mb-3">
          <MailX size={18} />
          <span className="text-[11px] tracking-[0.25em] uppercase">Disiscrizione</span>
        </div>
        <h1 className="font-serif font-light text-3xl text-[#1A1A1A] leading-tight">
          Non vuoi più ricevere promozioni?
        </h1>
        <p className="mt-3 text-sm text-[#4A4A4A] font-light">
          Conferma e <strong>{data.center}</strong> smetterà di inviarti offerte e novità via {data.channel_label}.
        </p>

        {error && <div className="mt-4 text-sm text-[#B91C1C]">{error}</div>}

        <button
          onClick={conferma}
          disabled={sending}
          data-testid="unsubscribe-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-50 text-white rounded-full px-6 py-3.5 text-sm tracking-wide transition-all"
        >
          {sending && <Loader2 size={16} className="animate-spin" />}
          Sì, disiscrivimi
        </button>

        <p className="mt-4 text-[11px] text-[#737373] leading-relaxed text-center">
          Per registrare la richiesta conserviamo data, ora, indirizzo IP e browser usato.
        </p>
      </div>
    </Wrap>
  );
}
